import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ImageList,
  ImageListItem,
  CircularProgress,
  Alert,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  AlertTitle
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import { format } from 'date-fns';
import { IndividualAnimal, MedicalRecord, AnimalGroup } from '../../../../types/animal';
import StatusChip from './StatusChip';
import { formatDate, calculateAge, getParentName, getOffspring } from '../../../../utils/animalUtils';

interface AnimalDetailsProps {
  currentAnimal: IndividualAnimal | null;
  selectedTab: number;
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  handlePhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEditAnimal: (animal: IndividualAnimal) => void;
  handleAddMedicalRecord: (animal: IndividualAnimal) => void;
  handleSetProfilePhoto: (index: number) => void;
  handleDeletePhoto: (index: number) => void;
  medicalRecords: MedicalRecord[];
  medicalRecordsLoading: boolean;
  handleOpenMedicalRecordDialog: (record: MedicalRecord) => void;
  animalGroups: AnimalGroup[];
  handleViewAnimal: (animal: IndividualAnimal) => void;
  setCarouselOpen: (open: boolean) => void;
  setActiveStep: (step: number) => void;
  bulkMedicalRecords: MedicalRecord[];
  bulkMedicalRecordsLoading: boolean;
  onEditBulkRecord?: (record: MedicalRecord) => void;
  onDeleteBulkRecord?: (id: string) => void;
  handleDeleteAnimal?: (animal: IndividualAnimal) => void;
}

const AnimalDetails: React.FC<AnimalDetailsProps> = ({
  currentAnimal,
  selectedTab,
  handleTabChange,
  handlePhotoUpload,
  handleEditAnimal,
  handleAddMedicalRecord,
  handleSetProfilePhoto,
  handleDeletePhoto,
  medicalRecords,
  medicalRecordsLoading,
  handleOpenMedicalRecordDialog,
  animalGroups,
  handleViewAnimal,
  setCarouselOpen,
  setActiveStep,
  bulkMedicalRecords,
  bulkMedicalRecordsLoading,
  onEditBulkRecord,
  onDeleteBulkRecord,
  handleDeleteAnimal
}) => {
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const uniqueBulkRecords = useMemo(() => {
    if (!bulkMedicalRecords) return [];
    const seen = new Set();
    return bulkMedicalRecords.filter(record => {
      const duplicate = seen.has(record.group_record_id);
      seen.add(record.group_record_id);
      return !duplicate;
    });
  }, [bulkMedicalRecords]);

  if (!currentAnimal) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Select an animal to view details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click on an animal from the list on the left to view its details, health records, and genealogy.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Medical Records" />
            <Tab label="Genealogy" />
            <Tab label="Bulk Records" />
          </Tabs>
        </Box>

        {/* Animal Overview */}
        {selectedTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  {currentAnimal.photos && currentAnimal.photos.length > 0 ? (
                    <Avatar 
                      sx={{ width: 56, height: 56 }} 
                      src={currentAnimal.photos[0]} 
                      alt={currentAnimal.name}
                    />
                  ) : (
                    <Avatar sx={{ width: 56, height: 56 }}>{currentAnimal.name.charAt(0)}</Avatar>
                  )}
                  <Box>
                    <Typography variant="h5">{currentAnimal.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentAnimal.breed} • ID: {currentAnimal.tagId || 'No Tag'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="animal-photo-upload"
                    type="file"
                    onChange={handlePhotoUpload}
                  />
                  <label htmlFor="animal-photo-upload">
                    <IconButton 
                      component="span" 
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <Button 
                    startIcon={<EditIcon />} 
                    variant="outlined" 
                    size="small" 
                    onClick={() => handleEditAnimal(currentAnimal)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  {handleDeleteAnimal && (
                    <Button 
                      startIcon={<DeleteIcon />} 
                      variant="outlined" 
                      color="error"
                      size="small" 
                      onClick={() => handleDeleteAnimal(currentAnimal)}
                      sx={{ mr: 1 }}
                    >
                      Delete
                    </Button>
                  )}
                  <Button 
                    startIcon={<MedicalServicesIcon />} 
                    variant="contained" 
                    size="small" 
                    onClick={() => handleAddMedicalRecord(currentAnimal)}
                  >
                    Add Record
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            {/* Photo Gallery Section */}
            {currentAnimal.photos && currentAnimal.photos.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Photos</Typography>
                
                <Grid container spacing={2}>
                  {currentAnimal.photos.map((photo, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 180,
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.9,
                            '& .actions': {
                              display: 'flex',
                            },
                          },
                        }}
                        onClick={() => {
                          setActiveStep(index);
                          setCarouselOpen(true);
                        }}
                      >
                        <Box
                          component="img"
                          src={photo}
                          alt={`${currentAnimal.name} photo ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <Box
                          className="actions"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            display: 'none',
                            justifyContent: 'space-around',
                            p: 0.5,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Set as profile photo">
                            <IconButton 
                              size="small" 
                              sx={{ color: 'white' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetProfilePhoto(index);
                              }}
                            >
                              <PhotoCamera fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete photo">
                            <IconButton 
                              size="small" 
                              sx={{ color: 'white' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePhoto(index);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {index === 0 && (
                          <Chip 
                            label="Profile Photo" 
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              bgcolor: 'rgba(0, 0, 0, 0.6)',
                              color: 'white',
                              fontSize: '0.6rem'
                            }}
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                  <Grid item xs={6} sm={4} md={3}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 180,
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column'
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="gallery-photo-upload"
                        multiple
                        onChange={handlePhotoUpload}
                      />
                      <label htmlFor="gallery-photo-upload" style={{ textAlign: 'center' }}>
                        <IconButton component="span">
                          <AddPhotoAlternateIcon />
                        </IconButton>
                        <Typography variant="caption" display="block">
                          Add Photo
                        </Typography>
                      </label>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Details Grid */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Basic Information</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <StatusChip
                    label={currentAnimal.status ? currentAnimal.status.charAt(0).toUpperCase() + currentAnimal.status.slice(1) : 'Unknown'}
                    size="small" 
                    status={currentAnimal.status}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1">
                    {currentAnimal.gender === 'male' ? '♂ Male' : '♀ Female'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Birth Date</Typography>
                  <Typography variant="body1">{formatDate(currentAnimal.birthDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Age</Typography>
                  <Typography variant="body1">{calculateAge(currentAnimal.birthDate)}</Typography>
                </Grid>
                {currentAnimal.weight && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Weight</Typography>
                    <Typography variant="body1">
                      {currentAnimal.weight} {currentAnimal.weightUnit || 'kg'}
                    </Typography>
                  </Grid>
                )}
                {currentAnimal.assetValue && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Asset Value</Typography>
                    <Typography variant="body1">
                      Ksh {currentAnimal.assetValue.toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Yield Information</Typography>
              {(!currentAnimal.yields || currentAnimal.yields.length === 0) ? (
                <Typography variant="body2" color="text.secondary">No yield information recorded.</Typography>
              ) : (
                <Grid container spacing={1}>
                  {currentAnimal.yields.map((y, idx) => (
                    <Grid item xs={12} key={idx} sx={{ mb: 1 }}>
                      <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="primary">{y.yieldType}</Typography>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">{y.dailyAvgYield} {y.yieldUnit} / day</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            Ksh {(y.dailyAvgYield * y.marketPricePerUnit * 30).toLocaleString()} / month
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Price: Ksh {y.marketPricePerUnit} per {y.yieldUnit}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="success.main" sx={{ mt: 1 }}>
                      Total Est. Monthly: Ksh {(currentAnimal.yields.reduce((sum, y) => 
                        sum + (y.dailyAvgYield * y.marketPricePerUnit * 30), 0)).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Health Information</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Last Health Check</Typography>
                  <Typography variant="body1">
                    {currentAnimal.lastHealthCheck ? formatDate(currentAnimal.lastHealthCheck) : 'None recorded'}
                  </Typography>
                </Grid>
                {currentAnimal.status === 'sick' && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <AlertTitle>Health Alert</AlertTitle>
                      This animal requires attention - {
                        currentAnimal.medicalRecords
                          .filter(r => r.status === 'ongoing')
                          .map(r => r.title)
                          .join(', ')
                      }
                    </Alert>
                  </Grid>
                )}
                {currentAnimal.status === 'pregnant' && currentAnimal.expectedDeliveryDate && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <AlertTitle>Pregnancy</AlertTitle>
                      Expected delivery date: {formatDate(currentAnimal.expectedDeliveryDate)}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Notes</Typography>
              <Typography variant="body2" color="text.secondary">
                {currentAnimal.notes || 'No notes available.'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Recent Medical Records</Typography>
              {/* Use medicalRecords from DB, not currentAnimal.medicalRecords, for up-to-date records */}
              {medicalRecords.length > 0 ? (
                <List>
                  {medicalRecords
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .slice(0, 3)
                    .map((record) => (
                      <ListItemButton key={record.id}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: record.status === 'ongoing' ? 'warning.main' : 'primary.main' }}>
                            <MedicalServicesIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={record.title}
                          secondary={`${formatDate(record.date)} - ${record.description.substring(0, 80)}${record.description.length > 80 ? '...' : ''}`}
                        />
                      </ListItemButton>
                    ))
                  }
                  {medicalRecords.length > 3 && (
                    <Button 
                      onClick={(e) => handleTabChange(e, 1)} 
                      sx={{ mt: 1 }}
                    >
                      View all {medicalRecords.length} records
                    </Button>
                  )}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No medical records available for this animal.
                </Typography>
              )}
            </Grid>

            {/* Photos section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Animal Photos
              </Typography>
              <Box sx={{ mt: 1, mb: 2 }}>
                {currentAnimal?.photos && currentAnimal.photos.length > 0 ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {currentAnimal.photos.length} photo(s) attached. First photo will be used as profile picture.
                    </Typography>
                    <ImageList sx={{ maxHeight: 200, overflow: 'auto' }} cols={4} rowHeight={100}>
                      {currentAnimal.photos.map((photo, index) => (
                        <ImageListItem key={index}>
                          <img
                            src={photo}
                            alt={`${currentAnimal?.name || 'Animal'} photo ${index + 1}`}
                            loading="lazy"
                            style={{ height: 100, objectFit: 'cover' }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No photos attached. Upload photos to help identify the animal.
                  </Typography>
                )}
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="edit-animal-photo-upload"
                    type="file"
                    onChange={handlePhotoUpload}
                  />
                  <label htmlFor="edit-animal-photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<AddPhotoAlternateIcon />}
                      size="small"
                    >
                      Add Photo
                    </Button>
                  </label>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Medical Records Tab */}
        {selectedTab === 1 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Medical Records for {currentAnimal.name}
              </Typography>
              <Button 
                startIcon={<MedicalServicesIcon />} 
                variant="contained" 
                size="small" 
                onClick={() => handleAddMedicalRecord(currentAnimal)}
              >
                Add Record
              </Button>
            </Box>
            {medicalRecordsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : medicalRecords.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 120 }}>Date</TableCell>
                      <TableCell sx={{ width: 100 }}>Type</TableCell>
                      <TableCell sx={{ width: 200 }}>Title</TableCell>
                      <TableCell sx={{ width: 120 }}>Status</TableCell>
                      <TableCell sx={{ width: 150 }}>Performed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medicalRecords.map((record) => (
                      <TableRow
                        key={record.id}
                        hover
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleOpenMedicalRecordDialog(record)}
                        onMouseEnter={() => setHoveredRowId(record.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                      >
                        {/* Tooltip always appears above the date cell, triggered by hovering any cell in the row */}
                        <Tooltip
                          open={hoveredRowId === record.id}
                          title={
                            <>
                              <div><b>Description:</b> {record.description}</div>
                              {record.result && <div><b>Result:</b> {record.result}</div>}
                              {record.cost && <div><b>Cost:</b> Ksh {record.cost}</div>}
                              {record.followUpDate && <div><b>Follow-up:</b> {formatDate(record.followUpDate)}</div>}
                            </>
                          }
                          arrow
                          placement="top"
                        >
                          <TableCell sx={{ width: 120 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {formatDate(record.date)}
                            </Box>
                          </TableCell>
                        </Tooltip>
                        <TableCell sx={{ width: 100 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Chip 
                              label={record.type.charAt(0).toUpperCase() + record.type.slice(1)} 
                              size="small" 
                              color={
                                record.type === 'treatment' ? 'warning' : 
                                record.type === 'vaccination' ? 'success' : 
                                record.type === 'surgery' ? 'error' : 
                                'default'
                              }
                              sx={{ maxWidth: '100%' }}
                            />
                          </Box>
                          </TableCell>
                        <TableCell sx={{ width: 200 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {record.title}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: 120 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Chip 
                              label={record.status.charAt(0).toUpperCase() + record.status.slice(1)} 
                              size="small" 
                              color={
                                record.status === 'ongoing' ? 'warning' : 
                                record.status === 'scheduled' ? 'info' : 
                                'success'
                              }
                              sx={{ maxWidth: '100%' }}
                            />
                          </Box>
                          </TableCell>
                        <TableCell sx={{ width: 150 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {record.performedBy}
                          </Box>
                        </TableCell>
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No medical records available for this animal.
              </Alert>
            )}
          </>
        )}

        {/* Genealogy Tab */}
        {selectedTab === 2 && (
          <>
            <Typography variant="h6" gutterBottom>
              Genealogy for {currentAnimal.name}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" display="flex" alignItems="center" gap={1}>
                      <FamilyRestroomIcon /> Parents
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary="Father" 
                          secondary={getParentName(currentAnimal.parentMaleId, animalGroups)} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Mother" 
                          secondary={getParentName(currentAnimal.parentFemaleId, animalGroups)} 
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" display="flex" alignItems="center" gap={1}>
                      <ChildCareIcon /> Offspring
                    </Typography>
                    
                    {getOffspring(currentAnimal.id, animalGroups).length > 0 ? (
                      <List>
                        {getOffspring(currentAnimal.id, animalGroups).map(offspring => (
                          <ListItemButton 
                            key={offspring.id}
                            onClick={() => handleViewAnimal(offspring)}
                          >
                            <ListItemAvatar>
                              <Avatar>{offspring.name.charAt(0)}</Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                              primary={offspring.name} 
                              secondary={`${offspring.gender === 'male' ? '♂' : '♀'} • ${formatDate(offspring.birthDate)} • ${calculateAge(offspring.birthDate)}`} 
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" sx={{ p: 2 }} color="text.secondary">
                        No offspring records found for this animal.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
        {selectedTab === 3 && (
          <Box>
            {bulkMedicalRecordsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : uniqueBulkRecords.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Performed By</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uniqueBulkRecords.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{formatDate(record.date)}</TableCell>
                        <TableCell>{record.type}</TableCell>
                        <TableCell>{record.title}</TableCell>
                        <TableCell>{record.status}</TableCell>
                        <TableCell>{record.performedBy}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => onEditBulkRecord && onEditBulkRecord(record)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => onDeleteBulkRecord && onDeleteBulkRecord(record.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No bulk medical records for this group yet.</Alert>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AnimalDetails;
